import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import statusPageApi, { StatusPage, StatusPageIncident, IncidentUpdate } from '../../services/api/status-page.api';
import uptimeMonitorService, { UptimeMonitor } from '../../services/api/uptime-monitor.service';
import toast from 'react-hot-toast';

type ActiveTab = 'components' | 'incidents' | 'settings';

export default function StatusPageManagePage() {
  const { t } = useTranslation(['statusPages', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statusPage, setStatusPage] = useState<StatusPage | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('components');

  // Components state
  const [showAddComponentModal, setShowAddComponentModal] = useState(false);
  const [editingComponentId, setEditingComponentId] = useState<string | null>(null);
  const [componentFormData, setComponentFormData] = useState({
    name: '',
    description: '',
    status: 'operational' as const,
    monitoringEnabled: false,
    healthCheckUrl: '',
    checkInterval: 60,
    timeout: 10,
    expectedStatusCode: 200,
    uptime_monitor_id: '',
    monitoringType: 'manual' as 'manual' | 'health_check' | 'uptime_monitor',
  });
  const [creatingComponent, setCreatingComponent] = useState(false);
  const [components, setComponents] = useState<any[]>([]);
  const [uptimeMonitors, setUptimeMonitors] = useState<UptimeMonitor[]>([]);

  // Incidents state
  const [incidents, setIncidents] = useState<StatusPageIncident[]>([]);
  const [showAddIncidentModal, setShowAddIncidentModal] = useState(false);
  const [incidentFormData, setIncidentFormData] = useState({
    title: '',
    message: '',
    severity: 'minor' as const,
    status: 'investigating' as const,
  });
  const [creatingIncident, setCreatingIncident] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<StatusPageIncident | null>(null);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [updateFormData, setUpdateFormData] = useState({
    status: 'investigating' as const,
    message: '',
  });
  const [addingUpdate, setAddingUpdate] = useState(false);

  // Settings state
  const [settingsFormData, setSettingsFormData] = useState({
    name: '',
    public_url_slug: '',
    description: '',
    logo_url: '',
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadStatusPage();
      loadComponents();
      loadIncidents();
      loadUptimeMonitors();
    }
  }, [id]);

  const loadUptimeMonitors = async () => {
    try {
      const response = await uptimeMonitorService.getAll({ limit: 100 });
      if (response.success) {
        setUptimeMonitors(response.data);
      }
    } catch (error) {
      console.error('Error loading uptime monitors:', error);
    }
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  const loadStatusPage = async () => {
    try {
      setLoading(true);
      const response = await statusPageApi.getById(id!);
      if (response.success) {
        setStatusPage(response.data);
        // Populate settings form
        setSettingsFormData({
          name: response.data.name || '',
          public_url_slug: response.data.public_url_slug || '',
          description: response.data.description || '',
          logo_url: response.data.logo_url || '',
        });
      }
    } catch (error) {
      console.error('Error loading status page:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadComponents = async () => {
    try {
      const response = await statusPageApi.getComponents(id!);
      if (response.success) {
        setComponents(response.data);
      }
    } catch (error) {
      console.error('Error loading components:', error);
    }
  };

  const handleCreateComponent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creatingComponent) return;

    setCreatingComponent(true);
    try {
      const payload: any = {
        name: componentFormData.name,
        description: componentFormData.description,
        status: componentFormData.status,
      };

      // Add uptime monitor link if selected
      if (componentFormData.monitoringType === 'uptime_monitor' && componentFormData.uptime_monitor_id) {
        payload.uptime_monitor_id = componentFormData.uptime_monitor_id;
      }

      // Add health check metadata if monitoring is enabled
      if (componentFormData.monitoringType === 'health_check' && componentFormData.healthCheckUrl) {
        payload.metadata = {
          health_check: {
            enabled: true,
            url: componentFormData.healthCheckUrl,
            interval_seconds: componentFormData.checkInterval,
            timeout_seconds: componentFormData.timeout,
            expected_status_code: componentFormData.expectedStatusCode,
            degraded_threshold_ms: 2000,
          },
        };
      }

      const response = await statusPageApi.createComponent(id!, payload);
      if (response.success) {
        setShowAddComponentModal(false);
        setComponentFormData({
          name: '',
          description: '',
          status: 'operational',
          monitoringEnabled: false,
          healthCheckUrl: '',
          checkInterval: 60,
          timeout: 10,
          expectedStatusCode: 200,
          uptime_monitor_id: '',
          monitoringType: 'manual',
        });
        await loadComponents();
      }
    } catch (error: any) {
      console.error('Error creating component:', error);
      toast.error(error.response?.data?.message || t('common:messages.error'), {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setCreatingComponent(false);
    }
  };

  const handleDeleteComponent = async (componentId: string) => {
    if (!confirm(t('statusPages:manage.components.confirmDelete'))) return;

    try {
      await statusPageApi.deleteComponent(id!, componentId);
      await loadComponents();
    } catch (error) {
      console.error('Error deleting component:', error);
    }
  };

  const handleEditComponent = (component: any) => {
    const metadata = component.metadata ? (typeof component.metadata === 'string' ? JSON.parse(component.metadata) : component.metadata) : null;
    const healthCheck = metadata?.health_check;

    // Determine monitoring type
    let monitoringType: 'manual' | 'health_check' | 'uptime_monitor' = 'manual';
    if (component.uptime_monitor_id) {
      monitoringType = 'uptime_monitor';
    } else if (healthCheck?.enabled) {
      monitoringType = 'health_check';
    }

    setEditingComponentId(component.id);
    setComponentFormData({
      name: component.name || '',
      description: component.description || '',
      status: component.status || 'operational',
      monitoringEnabled: healthCheck?.enabled === true,
      healthCheckUrl: healthCheck?.url || '',
      checkInterval: healthCheck?.interval_seconds || 60,
      timeout: healthCheck?.timeout_seconds || 10,
      expectedStatusCode: healthCheck?.expected_status_code || 200,
      uptime_monitor_id: component.uptime_monitor_id || '',
      monitoringType,
    });
    setShowAddComponentModal(true);
  };

  const handleUpdateComponent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creatingComponent || !editingComponentId) return;

    setCreatingComponent(true);
    try {
      const payload: any = {
        name: componentFormData.name,
        description: componentFormData.description,
        status: componentFormData.status,
      };

      // Handle uptime monitor link
      if (componentFormData.monitoringType === 'uptime_monitor' && componentFormData.uptime_monitor_id) {
        payload.uptime_monitor_id = componentFormData.uptime_monitor_id;
        payload.metadata = null;
      } else if (componentFormData.monitoringType === 'health_check' && componentFormData.healthCheckUrl) {
        payload.uptime_monitor_id = null;
        payload.metadata = {
          health_check: {
            enabled: true,
            url: componentFormData.healthCheckUrl,
            interval_seconds: componentFormData.checkInterval,
            timeout_seconds: componentFormData.timeout,
            expected_status_code: componentFormData.expectedStatusCode,
            degraded_threshold_ms: 2000,
          },
        };
      } else {
        payload.uptime_monitor_id = null;
        payload.metadata = null;
      }

      const response = await statusPageApi.updateComponent(id!, editingComponentId, payload);
      if (response.success) {
        setShowAddComponentModal(false);
        setEditingComponentId(null);
        setComponentFormData({
          name: '',
          description: '',
          status: 'operational',
          monitoringEnabled: false,
          healthCheckUrl: '',
          checkInterval: 60,
          timeout: 10,
          expectedStatusCode: 200,
          uptime_monitor_id: '',
          monitoringType: 'manual',
        });
        await loadComponents();
      }
    } catch (error: any) {
      console.error('Error updating component:', error);
      toast.error(error.response?.data?.message || t('common:messages.error'), {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setCreatingComponent(false);
    }
  };

  const closeComponentModal = () => {
    setShowAddComponentModal(false);
    setEditingComponentId(null);
    setComponentFormData({
      name: '',
      description: '',
      status: 'operational',
      monitoringEnabled: false,
      healthCheckUrl: '',
      checkInterval: 60,
      timeout: 10,
      expectedStatusCode: 200,
      uptime_monitor_id: '',
      monitoringType: 'manual',
    });
  };

  // Incident functions
  const loadIncidents = async () => {
    try {
      const response = await statusPageApi.getIncidents(id!, { includeResolved: true });
      if (response.success) {
        setIncidents(response.data);
      }
    } catch (error) {
      console.error('Error loading incidents:', error);
    }
  };

  const handleCreateIncident = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creatingIncident) return;

    setCreatingIncident(true);
    try {
      const response = await statusPageApi.createIncident(id!, incidentFormData);
      if (response.success) {
        setShowAddIncidentModal(false);
        setIncidentFormData({ title: '', message: '', severity: 'minor', status: 'investigating' });
        await loadIncidents();
      }
    } catch (error: any) {
      console.error('Error creating incident:', error);
      toast.error(error.response?.data?.message || t('common:messages.error'), {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setCreatingIncident(false);
    }
  };

  const handleAddUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (addingUpdate || !selectedIncident) return;

    setAddingUpdate(true);
    try {
      const response = await statusPageApi.addIncidentUpdate(id!, selectedIncident.id, updateFormData);
      if (response.success) {
        setShowAddUpdateModal(false);
        setUpdateFormData({ status: 'investigating', message: '' });
        await loadIncidents();
        // Reload the selected incident to get updates
        setSelectedIncident(null);
      }
    } catch (error: any) {
      console.error('Error adding update:', error);
      toast.error(error.response?.data?.message || t('common:messages.error'), {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setAddingUpdate(false);
    }
  };

  const handleResolveIncident = async (incidentId: string) => {
    const message = prompt(t('statusPages:manage.incidents.resolvePrompt'));
    if (!message) return;

    try {
      await statusPageApi.resolveIncident(id!, incidentId, message);
      await loadIncidents();
      if (selectedIncident?.id === incidentId) {
        setSelectedIncident(null);
      }
    } catch (error) {
      console.error('Error resolving incident:', error);
      toast.error(t('statusPages:manage.incidents.resolveError'), {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const handleDeleteIncident = async (incidentId: string) => {
    if (!confirm(t('statusPages:manage.incidents.confirmDelete'))) return;

    try {
      await statusPageApi.deleteIncident(id!, incidentId);
      await loadIncidents();
      if (selectedIncident?.id === incidentId) {
        setSelectedIncident(null);
      }
    } catch (error) {
      console.error('Error deleting incident:', error);
    }
  };

  // Settings functions
  const handleLogoFileChange = async (file: File) => {
    if (!id) return;

    setSelectedLogoFile(file);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setLogoPreviewUrl(previewUrl);

    // Upload automatically
    setUploadingLogo(true);
    try {
      const response = await statusPageApi.uploadLogo(id, file);
      console.log('[Frontend] Upload response:', response);

      if (response.success) {
        // Clean up preview URL
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setLogoPreviewUrl(null);
        }
        setSelectedLogoFile(null);

        // Reload the status page to get fresh data from database
        await loadStatusPage();

        toast.success('Logo uploaded successfully!', {
          duration: 3000,
          icon: '✅',
        });
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      toast.error(error.response?.data?.message || 'Failed to upload logo', {
        duration: 5000,
        icon: '❌',
      });
      // Keep preview on error so user can try again
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingSettings) return;

    setSavingSettings(true);
    try {
      const response = await statusPageApi.update(id!, settingsFormData);
      if (response.success) {
        setStatusPage(response.data);
        toast.success(t('statusPages:manage.settings.saveSuccess'), {
          duration: 3000,
          icon: '✅',
        });
      }
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.message || t('common:messages.error'), {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'partial_outage':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'major_outage':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'maintenance':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getIncidentStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'monitoring':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'identified':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'investigating':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'major':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'minor':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  if (!statusPage) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {t('statusPages:manage.notFound')}
          </h2>
          <button
            onClick={() => navigate('/status-pages')}
            className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
          >
            {t('statusPages:manage.backToList')}
          </button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-8">
        <button
          onClick={() => navigate('/status-pages')}
          className="mb-4 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          {t('statusPages:manage.backButton')}
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {statusPage.name}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {statusPage.description || t('statusPages:manage.noDescription')}
        </p>
      </div>

      {/* Status Page Details */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 mb-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('statusPages:manage.details.publicUrl')}
          </h3>
          <a
            href={`/status/${statusPage.public_url_slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-lg font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
          >
            /status/{statusPage.public_url_slug}
          </a>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('statusPages:manage.details.status')}
          </h3>
          <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
            statusPage.is_enabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            {statusPage.is_enabled ? t('statusPages:active') : t('statusPages:disabled')}
          </span>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('statusPages:manage.details.created')}
          </h3>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {new Date(statusPage.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="border-b border-gray-200 dark:border-gray-800">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('components')}
              className={`border-b-2 py-4 px-1 text-sm font-medium ${
                activeTab === 'components'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              {t('statusPages:manage.tabs.components')}
            </button>
            <button
              onClick={() => setActiveTab('incidents')}
              className={`border-b-2 py-4 px-1 text-sm font-medium ${
                activeTab === 'incidents'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              {t('statusPages:manage.tabs.incidents')}
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`border-b-2 py-4 px-1 text-sm font-medium ${
                activeTab === 'settings'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              {t('statusPages:manage.tabs.settings')}
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Components Tab */}
          {activeTab === 'components' && (
            <>
              {components.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t('statusPages:manage.components.noComponents')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {t('statusPages:manage.components.noComponentsDesc')}
                  </p>
                  <button
                    onClick={() => setShowAddComponentModal(true)}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    {t('statusPages:manage.components.addButton')}
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('statusPages:manage.components.count', { count: components.length })}
                    </h3>
                    <button
                      onClick={() => setShowAddComponentModal(true)}
                      className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                    >
                      {t('statusPages:manage.components.addButton')}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {components.map((component: any) => (
                      <div
                        key={component.id}
                        className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {component.name}
                            </h4>
                            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(component.status)}`}>
                              {component.status.replace('_', ' ')}
                            </span>
                          </div>
                          {component.description && (
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                              {component.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditComponent(component)}
                            className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                          >
                            {t('statusPages:manage.components.edit')}
                          </button>
                          <button
                            onClick={() => handleDeleteComponent(component.id)}
                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400"
                          >
                            {t('statusPages:manage.components.delete')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Incidents Tab */}
          {activeTab === 'incidents' && (
            <>
              {!selectedIncident ? (
                <>
                  {incidents.length === 0 ? (
                    <div className="text-center py-12">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {t('statusPages:manage.incidents.noIncidents')}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {t('statusPages:manage.incidents.noIncidentsDesc')}
                      </p>
                      <button
                        onClick={() => setShowAddIncidentModal(true)}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                      >
                        {t('statusPages:manage.incidents.reportButton')}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('statusPages:manage.incidents.count', { count: incidents.length })}
                        </h3>
                        <button
                          onClick={() => setShowAddIncidentModal(true)}
                          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                        >
                          {t('statusPages:manage.incidents.reportButton')}
                        </button>
                      </div>

                      <div className="space-y-3">
                        {incidents.map((incident) => (
                          <div
                            key={incident.id}
                            className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 cursor-pointer hover:border-indigo-500 dark:hover:border-indigo-500"
                            onClick={() => setSelectedIncident(incident)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <h4 className="font-medium text-gray-900 dark:text-white">
                                    {incident.title}
                                  </h4>
                                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getIncidentStatusColor(incident.status)}`}>
                                    {incident.status}
                                  </span>
                                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getSeverityColor(incident.severity)}`}>
                                    {incident.severity}
                                  </span>
                                </div>
                                {incident.message && (
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {incident.message}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 dark:text-gray-500">
                                  {t('statusPages:manage.incidents.started')}: {new Date(incident.started_at).toLocaleString()}
                                  {incident.resolved_at && ` • ${t('statusPages:manage.incidents.resolved')}: ${new Date(incident.resolved_at).toLocaleString()}`}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <button
                    onClick={() => setSelectedIncident(null)}
                    className="mb-4 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                  >
                    {t('statusPages:manage.incidents.backToIncidents')}
                  </button>

                  <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {selectedIncident.title}
                          </h2>
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getIncidentStatusColor(selectedIncident.status)}`}>
                            {selectedIncident.status}
                          </span>
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getSeverityColor(selectedIncident.severity)}`}>
                            {selectedIncident.severity}
                          </span>
                        </div>
                        {selectedIncident.message && (
                          <p className="text-gray-600 dark:text-gray-400 mb-2">
                            {selectedIncident.message}
                          </p>
                        )}
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          {t('statusPages:manage.incidents.started')}: {new Date(selectedIncident.started_at).toLocaleString()}
                          {selectedIncident.resolved_at && ` • ${t('statusPages:manage.incidents.resolved')}: ${new Date(selectedIncident.resolved_at).toLocaleString()}`}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        {selectedIncident.status !== 'resolved' && (
                          <>
                            <button
                              onClick={() => setShowAddUpdateModal(true)}
                              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                            >
                              {t('statusPages:manage.incidents.addUpdate')}
                            </button>
                            <button
                              onClick={() => handleResolveIncident(selectedIncident.id)}
                              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                            >
                              {t('statusPages:manage.incidents.resolve')}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteIncident(selectedIncident.id)}
                          className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                        >
                          {t('statusPages:manage.incidents.delete')}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      {t('statusPages:manage.incidents.updatesTimeline')}
                    </h3>
                    {selectedIncident.updates && Array.isArray(selectedIncident.updates) && selectedIncident.updates.length > 0 ? (
                      <div className="space-y-4">
                        {selectedIncident.updates.map((update: any) => (
                          <div
                            key={update.id}
                            className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                          >
                            <div className="flex items-center space-x-3 mb-2">
                              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getIncidentStatusColor(update.status)}`}>
                                {update.status}
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-500">
                                {new Date(update.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-gray-900 dark:text-white">{update.message}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-600 dark:text-gray-400">{t('statusPages:manage.incidents.noUpdates')}</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                {t('statusPages:manage.settings.title')}
              </h3>
              <form onSubmit={handleSaveSettings}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('statusPages:manage.settings.nameRequired')}
                    </label>
                    <input
                      type="text"
                      required
                      disabled={savingSettings}
                      value={settingsFormData.name}
                      onChange={(e) => setSettingsFormData({ ...settingsFormData, name: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                      placeholder={t('statusPages:manage.settings.placeholder.name')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Logo
                    </label>

                    {/* Current Logo */}
                    {settingsFormData.logo_url && !logoPreviewUrl && (
                      <div className="mb-3 flex items-center space-x-3">
                        <img
                          src={settingsFormData.logo_url}
                          alt="Current logo"
                          className="h-16 w-auto rounded border border-gray-300 dark:border-gray-700 bg-white p-2"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <div>
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Current logo</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Upload a new file to replace</p>
                        </div>
                      </div>
                    )}

                    {/* Preview of Selected File */}
                    {logoPreviewUrl && (
                      <div className="mb-3 p-4 border-2 border-dashed border-indigo-300 dark:border-indigo-600 rounded-lg bg-indigo-50 dark:bg-indigo-900/10">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <img
                              src={logoPreviewUrl}
                              alt="Preview"
                              className="h-16 w-auto rounded border border-indigo-300 dark:border-indigo-600 bg-white p-2"
                            />
                            <div>
                              <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">Preview</span>
                              <p className="text-xs text-indigo-600 dark:text-indigo-400">{selectedLogoFile?.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {selectedLogoFile && `${(selectedLogoFile.size / 1024).toFixed(1)} KB`}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (logoPreviewUrl) {
                                URL.revokeObjectURL(logoPreviewUrl);
                              }
                              setLogoPreviewUrl(null);
                              setSelectedLogoFile(null);
                            }}
                            className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title="Remove preview"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingLogo}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleLogoFileChange(file);
                          }
                        }}
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                      />
                      {uploadingLogo && (
                        <div className="flex items-center space-x-2 text-indigo-600 dark:text-indigo-400">
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm font-medium">Uploading...</span>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Recommended: PNG or SVG, max 5MB. The logo will appear next to your status page name.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('statusPages:manage.settings.publicUrlSlugRequired')}
                    </label>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">/status/</span>
                      <input
                        type="text"
                        required
                        disabled={savingSettings}
                        value={settingsFormData.public_url_slug}
                        onChange={(e) => setSettingsFormData({ ...settingsFormData, public_url_slug: e.target.value })}
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                        placeholder={t('statusPages:manage.settings.placeholder.slug')}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                      {t('statusPages:manage.settings.urlHelp')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('statusPages:manage.settings.description')}
                    </label>
                    <textarea
                      disabled={savingSettings}
                      value={settingsFormData.description}
                      onChange={(e) => setSettingsFormData({ ...settingsFormData, description: e.target.value })}
                      rows={4}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                      placeholder={t('statusPages:manage.settings.placeholder.description')}
                    />
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('statusPages:manage.settings.statusLabel')}: {statusPage?.is_enabled ? (
                          <span className="text-green-600 dark:text-green-400 font-medium">{t('statusPages:manage.settings.enabled')}</span>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400 font-medium">{t('statusPages:manage.settings.disabled')}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {t('statusPages:manage.settings.createdLabel')} {new Date(statusPage?.created_at || '').toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      type="submit"
                      disabled={savingSettings}
                      className="rounded-lg bg-indigo-600 px-6 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center space-x-2"
                    >
                      {savingSettings && (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      <span>{savingSettings ? t('statusPages:manage.settings.saving') : t('statusPages:manage.settings.save')}</span>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Component Modal */}
      {showAddComponentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 dark:bg-gray-900">
            <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
              {editingComponentId ? t('statusPages:manage.components.modal.editTitle') : t('statusPages:manage.components.modal.addTitle')}
            </h2>
            <form onSubmit={editingComponentId ? handleUpdateComponent : handleCreateComponent}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:manage.components.modal.nameRequired')}
                  </label>
                  <input
                    type="text"
                    required
                    disabled={creatingComponent}
                    value={componentFormData.name}
                    onChange={(e) => setComponentFormData({ ...componentFormData, name: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                    placeholder={t('statusPages:manage.components.modal.placeholder.name')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:manage.components.modal.description')}
                  </label>
                  <textarea
                    disabled={creatingComponent}
                    value={componentFormData.description}
                    onChange={(e) => setComponentFormData({ ...componentFormData, description: e.target.value })}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                    placeholder={t('statusPages:manage.components.modal.monitoring.placeholder.description')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:manage.components.modal.status')}
                  </label>
                  <select
                    disabled={creatingComponent}
                    value={componentFormData.status}
                    onChange={(e) => setComponentFormData({ ...componentFormData, status: e.target.value as any })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                  >
                    <option value="operational">{t('statusPages:manage.components.modal.statuses.operational')}</option>
                    <option value="degraded">{t('statusPages:manage.components.modal.statuses.degraded')}</option>
                    <option value="partial_outage">{t('statusPages:manage.components.modal.statuses.partial_outage')}</option>
                    <option value="major_outage">{t('statusPages:manage.components.modal.statuses.major_outage')}</option>
                    <option value="maintenance">{t('statusPages:manage.components.modal.statuses.maintenance')}</option>
                  </select>
                </div>

                {/* Monitoring Section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('statusPages:manage.components.modal.monitoring.title')}
                  </label>

                  {/* Monitoring Type Selection */}
                  <div className="flex flex-col space-y-2 mb-4">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="monitoringType"
                        value="manual"
                        checked={componentFormData.monitoringType === 'manual'}
                        onChange={() => setComponentFormData({ ...componentFormData, monitoringType: 'manual' })}
                        className="form-radio h-4 w-4 text-indigo-600"
                        disabled={creatingComponent}
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('statusPages:manage.components.modal.monitoring.manual')}</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="monitoringType"
                        value="uptime_monitor"
                        checked={componentFormData.monitoringType === 'uptime_monitor'}
                        onChange={() => setComponentFormData({ ...componentFormData, monitoringType: 'uptime_monitor' })}
                        className="form-radio h-4 w-4 text-indigo-600"
                        disabled={creatingComponent}
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('statusPages:manage.components.modal.monitoring.useUptimeMonitor')}</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="monitoringType"
                        value="health_check"
                        checked={componentFormData.monitoringType === 'health_check'}
                        onChange={() => setComponentFormData({ ...componentFormData, monitoringType: 'health_check' })}
                        className="form-radio h-4 w-4 text-indigo-600"
                        disabled={creatingComponent}
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('statusPages:manage.components.modal.monitoring.customHealthCheck')}</span>
                    </label>
                  </div>

                  {/* Uptime Monitor Selection */}
                  {componentFormData.monitoringType === 'uptime_monitor' && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('statusPages:manage.components.modal.monitoring.selectUptimeMonitor')}
                      </label>
                      <select
                        disabled={creatingComponent}
                        value={componentFormData.uptime_monitor_id}
                        onChange={(e) => setComponentFormData({ ...componentFormData, uptime_monitor_id: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                      >
                        <option value="">{t('statusPages:manage.components.modal.monitoring.selectMonitor')}</option>
                        {uptimeMonitors.map((monitor) => (
                          <option key={monitor.id} value={monitor.id}>
                            {monitor.name} ({monitor.url}) - {monitor.status}
                          </option>
                        ))}
                      </select>
                      {uptimeMonitors.length === 0 && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          {t('statusPages:manage.components.modal.monitoring.noMonitorsAvailable')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Custom Health Check Fields */}
                  {componentFormData.monitoringType === 'health_check' && (
                    <div className="space-y-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('statusPages:manage.components.modal.monitoring.healthCheckUrlRequired')}
                        </label>
                        <input
                          type="url"
                          required={componentFormData.monitoringType === 'health_check'}
                          disabled={creatingComponent}
                          value={componentFormData.healthCheckUrl}
                          onChange={(e) => setComponentFormData({ ...componentFormData, healthCheckUrl: e.target.value })}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                          placeholder={t('statusPages:manage.components.modal.monitoring.placeholder.url')}
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('statusPages:manage.components.modal.monitoring.interval')}
                          </label>
                          <input
                            type="number"
                            min="30"
                            max="3600"
                            disabled={creatingComponent}
                            value={componentFormData.checkInterval}
                            onChange={(e) => setComponentFormData({ ...componentFormData, checkInterval: parseInt(e.target.value) })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('statusPages:manage.components.modal.monitoring.timeout')}
                          </label>
                          <input
                            type="number"
                            min="5"
                            max="60"
                            disabled={creatingComponent}
                            value={componentFormData.timeout}
                            onChange={(e) => setComponentFormData({ ...componentFormData, timeout: parseInt(e.target.value) })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('statusPages:manage.components.modal.monitoring.statusCode')}
                          </label>
                          <input
                            type="number"
                            min="200"
                            max="599"
                            disabled={creatingComponent}
                            value={componentFormData.expectedStatusCode}
                            onChange={(e) => setComponentFormData({ ...componentFormData, expectedStatusCode: parseInt(e.target.value) })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                          />
                        </div>
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('statusPages:manage.components.modal.monitoring.help', { interval: componentFormData.checkInterval })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  disabled={creatingComponent}
                  onClick={closeComponentModal}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  {t('statusPages:manage.components.modal.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={creatingComponent}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {creatingComponent && (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>
                    {creatingComponent
                      ? (editingComponentId ? t('statusPages:manage.components.modal.updating') : t('statusPages:manage.components.modal.adding'))
                      : (editingComponentId ? t('statusPages:manage.components.modal.update') : t('statusPages:manage.components.modal.add'))}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Incident Modal */}
      {showAddIncidentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 dark:bg-gray-900">
            <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
              {t('statusPages:manage.incidents.reportModal.title')}
            </h2>
            <form onSubmit={handleCreateIncident}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:manage.incidents.reportModal.titleRequired')}
                  </label>
                  <input
                    type="text"
                    required
                    disabled={creatingIncident}
                    value={incidentFormData.title}
                    onChange={(e) => setIncidentFormData({ ...incidentFormData, title: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                    placeholder={t('statusPages:manage.incidents.reportModal.placeholder.title')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:manage.incidents.reportModal.message')}
                  </label>
                  <textarea
                    disabled={creatingIncident}
                    value={incidentFormData.message}
                    onChange={(e) => setIncidentFormData({ ...incidentFormData, message: e.target.value })}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                    placeholder={t('statusPages:manage.incidents.reportModal.placeholder.message')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:manage.incidents.reportModal.severity')}
                  </label>
                  <select
                    disabled={creatingIncident}
                    value={incidentFormData.severity}
                    onChange={(e) => setIncidentFormData({ ...incidentFormData, severity: e.target.value as any })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                  >
                    <option value="minor">{t('statusPages:manage.incidents.severities.minor')}</option>
                    <option value="major">{t('statusPages:manage.incidents.severities.major')}</option>
                    <option value="critical">{t('statusPages:manage.incidents.severities.critical')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:manage.incidents.reportModal.initialStatus')}
                  </label>
                  <select
                    disabled={creatingIncident}
                    value={incidentFormData.status}
                    onChange={(e) => setIncidentFormData({ ...incidentFormData, status: e.target.value as any })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                  >
                    <option value="investigating">{t('statusPages:manage.incidents.statuses.investigating')}</option>
                    <option value="identified">{t('statusPages:manage.incidents.statuses.identified')}</option>
                    <option value="monitoring">{t('statusPages:manage.incidents.statuses.monitoring')}</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  disabled={creatingIncident}
                  onClick={() => {
                    setShowAddIncidentModal(false);
                    setIncidentFormData({ title: '', message: '', severity: 'minor', status: 'investigating' });
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  {t('statusPages:manage.incidents.reportModal.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={creatingIncident}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {creatingIncident && (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>{creatingIncident ? t('statusPages:manage.incidents.reportModal.reporting') : t('statusPages:manage.incidents.reportModal.report')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Update Modal */}
      {showAddUpdateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 dark:bg-gray-900">
            <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
              {t('statusPages:manage.incidents.updateModal.title')}
            </h2>
            <form onSubmit={handleAddUpdate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:manage.incidents.updateModal.statusRequired')}
                  </label>
                  <select
                    required
                    disabled={addingUpdate}
                    value={updateFormData.status}
                    onChange={(e) => setUpdateFormData({ ...updateFormData, status: e.target.value as any })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                  >
                    <option value="investigating">{t('statusPages:manage.incidents.statuses.investigating')}</option>
                    <option value="identified">{t('statusPages:manage.incidents.statuses.identified')}</option>
                    <option value="monitoring">{t('statusPages:manage.incidents.statuses.monitoring')}</option>
                    <option value="resolved">{t('statusPages:manage.incidents.statuses.resolved')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('statusPages:manage.incidents.updateModal.messageRequired')}
                  </label>
                  <textarea
                    required
                    disabled={addingUpdate}
                    value={updateFormData.message}
                    onChange={(e) => setUpdateFormData({ ...updateFormData, message: e.target.value })}
                    rows={4}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                    placeholder={t('statusPages:manage.incidents.updateModal.placeholder.message')}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  disabled={addingUpdate}
                  onClick={() => {
                    setShowAddUpdateModal(false);
                    setUpdateFormData({ status: 'investigating', message: '' });
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  {t('statusPages:manage.incidents.updateModal.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={addingUpdate}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {addingUpdate && (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>{addingUpdate ? t('statusPages:manage.incidents.updateModal.adding') : t('statusPages:manage.incidents.updateModal.add')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
